import { useEffect, useRef } from 'react';
import { parsePushPayload } from '../services/pushPayload';
import type { PushDeepLinkMeta } from '../services/pushDeepLink';

type PushAdapter = {
  name: string;
  requestPermission?: () => Promise<'granted' | 'denied' | 'default'>;
  getToken: () => Promise<string | null>;
  onMessage: (callback: (payload: unknown) => void) => () => void;
  saveToken: (token: string, userId: string) => Promise<void>;
};

type UsePushNotificationsOptions = {
  pushAdapter: PushAdapter | null | undefined;
  userId: string | undefined;
  push: (fields: Record<string, unknown>, source?: string) => void;
  pushEnabled: boolean;
  /** Called when the user taps an OS/notification tray alert (app was closed or backgrounded). */
  onPushTap?: (meta: PushDeepLinkMeta) => void;
};

/**
 * Registers FCM/native token when permission is already granted, and forwards
 * foreground messages into the in-app inbox. Does not prompt for permission —
 * call `requestPermission` from a user gesture (e.g. Settings toggle).
 */
export function usePushNotifications({
  pushAdapter,
  userId,
  push,
  pushEnabled,
  onPushTap,
}: UsePushNotificationsOptions) {
  const pushRef = useRef(push);
  const onPushTapRef = useRef(onPushTap);

  useEffect(() => {
    pushRef.current = push;
    onPushTapRef.current = onPushTap;
  });

  useEffect(() => {
    if (!pushAdapter || !userId || !pushEnabled) return undefined;

    let cancelled = false;
    let unsub = () => {};

    (async () => {
      try {
        const token = await pushAdapter.getToken();
        if (!cancelled && token) {
          await pushAdapter.saveToken(token, userId);
        }
      } catch (err) {
        console.warn('[Push] Token registration failed:', err);
      }

      if (cancelled) return;

      unsub = pushAdapter.onMessage((payload) => {
        const parsed = parsePushPayload(payload);
        const raw = (payload ?? {}) as { data?: Record<string, unknown> };
        const tapped = raw.data?.__tap === 'true' || raw.data?.__tap === true;
        pushRef.current(parsed, 'push');
        if (tapped && parsed.meta) {
          onPushTapRef.current?.(parsed.meta as PushDeepLinkMeta);
        }
      });
    })();

    return () => {
      cancelled = true;
      unsub();
    };
  }, [pushAdapter, userId, pushEnabled]);
}

/** Prompt for notification permission then register the device token (user gesture). */
export async function enablePushRegistration(
  pushAdapter: PushAdapter | null | undefined,
  userId: string | undefined,
): Promise<'granted' | 'denied' | 'default'> {
  if (!pushAdapter || !userId) return 'denied';
  const permission = pushAdapter.requestPermission
    ? await pushAdapter.requestPermission()
    : 'granted';
  if (permission !== 'granted') return permission;
  try {
    const token = await pushAdapter.getToken();
    if (token) await pushAdapter.saveToken(token, userId);
  } catch (err) {
    console.warn('[Push] Token registration after permission failed:', err);
  }
  return permission;
}
